import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { baseURL } from '../constants/base.url';
import { User } from '../models/auth.models';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;

  constructor(private http: HttpClient, private router: Router, private socketSvc: SocketService) {
    this.currentUserSubject = new BehaviorSubject<User | null>(
      JSON.parse(localStorage.getItem('authenticatedUser') || 'null')
    );
    this.currentUser = this.currentUserSubject.asObservable();

    const token = localStorage.getItem('authToken');
    if (token) {
      this.socketSvc.connect(token);
    }
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  // ✅ PIN Login method (POST: { pin: "1234" })
  loginWithPin(pin: string): Observable<any> {
    return this.http.post<any>(`${baseURL}auth/login`, { pin }).pipe(
      map(response => {
        // ✅ Your API returns: response.data.tokens.accessToken + response.data.user
        const accessToken = response?.data?.tokens?.accessToken;
        const user = response?.data?.user;

        if (accessToken && user) {
          localStorage.setItem('authToken', accessToken);
          localStorage.setItem('authenticatedUser', JSON.stringify(user));

          // ✅ correct keys
          localStorage.setItem('role', user.role);
          localStorage.setItem('userId', user.id || user._id); // ✅ FIX

          this.currentUserSubject.next(user);
          this.socketSvc.connect(accessToken);
        }

        return response;
      }),
      catchError(this.handleError)
    );
  }

  onLoginSuccess(accessToken: string) {
    this.socketSvc.connect(accessToken);
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  setToken(token: string, user: any): void {
    localStorage.setItem('authToken', token);
    localStorage.setItem('authenticatedUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  logout(): void {
    localStorage.removeItem('authenticatedUser');
    localStorage.removeItem('authToken');
    this.currentUserSubject.next(null);
    this.router.navigate(['/account/login']);
    this.socketSvc.disconnect();
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  private handleError(error: any) {
    let errorMessage = 'An unknown error occurred!';
    if (error?.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${error.error.message}`;
    } else {
      // try to use backend message if exists
      const backendMsg = error?.error?.message || error?.message;
      errorMessage = backendMsg
        ? `Server returned code: ${error.status}, message: ${backendMsg}`
        : `Server returned code: ${error.status}, error message is: ${error.message}`;
    }
    return throwError(() => errorMessage);
  }
}
